/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { VariableSizeList } from 'react-window';
import { debounce } from 'lodash';
import { message } from 'antd';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getLoadDetails } from '../../../../../store/actions/callCheckLoadsActions/callCheckLoads';
import { CallCheckLoadsSelectors } from '../../../../../store/selectors/callCheckLoadsSelectors';
import Row from './row';
import HeaderRow from './headerRow';
import { createSeparator } from '../../../../../store/reducers/callCheckLoads/helper/separator';
import Loading from '../../../screen/loading';
import { loadDetailsData } from './detailsHeaderInfo';
import SelectAndNotes from './SelectAndNotes';
import LumperModal from './LumperModal';

const SpreadsheetDetails = ({ resource, rowState, page, setLoadDetailsModalIsOpen }) => {
  const dispatch = useDispatch();
  const { state, loadDetails, loadDetailsLoading } =  CallCheckLoadsSelectors();
  const defRowHeight = 20;
  const assignedRef = useRef({});
  const loadId = rowState.type === 'stop' ? rowState?.loadId : rowState.id;
  const [ lumperModalIsOpen, setLumperModalIsOpen ] = useState(false);

  useEffect(() => {
    dispatch(getLoadDetails(resource, loadId, page));
  }, [getLoadDetails, resource, loadId, dispatch]);

  const listReference = useRef(null);
  const listHeaderReference = useRef(null);
  const mainScroll = useRef(null);
  const startOnScroll = width => (mainScroll.current.scrollLeft = width);

  const header = useCallback(
    (type) => (
      <div>
        <div
          ref={listHeaderReference}
          style={{
            width: '100%',
            overflow: 'hidden',
          }}>
          <HeaderRow resource={resource} state={state} type={type}/>
        </div>
      </div>
    ),
    [resource, state],
  );

  const displayLoadsAssignedPickStops = useMemo(
    () =>
      !loadDetails?.pickStopAndOrders || loadDetails?.pickStopAndOrders.length === 0
        ? [createSeparator(defRowHeight, state[resource].detailsPageHeadersPick, 'load', 0)]
        : loadDetails?.pickStopAndOrders,
    [loadDetails?.pickStopAndOrders, state, resource],
  );

  const displayLoadsAssignedDropStops = useMemo(
    () =>
      !loadDetails?.dropStopAndOrders || loadDetails?.dropStopAndOrders.length === 0
        ? [createSeparator(defRowHeight, state[resource].detailsPageHeadersDrop, 'load', 0)]
        : loadDetails?.dropStopAndOrders,
    [loadDetails?.dropStopAndOrders, state, resource],
  );

  const visibleDisplayLoadsAssignedPickStops = useMemo(
    () => displayLoadsAssignedPickStops.filter(order => order.rowState.visible),
    [displayLoadsAssignedPickStops],
  );

  const visibleDisplayLoadsAssignedDropStops = useMemo(
    () => displayLoadsAssignedDropStops.filter(order => order.rowState.visible),
    [displayLoadsAssignedDropStops],
  );

  const getPickItemSize = useCallback(
    index => {
      const item = visibleDisplayLoadsAssignedPickStops[index];
      switch (item?.rowState?.type) {
        case 'load':
        case 'dropLoad':
          return 20;
        default:
          return 20;
      }
    },
    [visibleDisplayLoadsAssignedPickStops],
  );

  const getDropItemSize = useCallback(
    index => {
      const item = visibleDisplayLoadsAssignedDropStops[index];
      switch (item?.rowState?.type) {
        case 'load':
        case 'dropLoad':
          return 20;
        default:
          return 20;
      }
    },
    [visibleDisplayLoadsAssignedDropStops],
  );

  const RenderPickRow = useCallback(
    ({ data, index, style }) =>
      index !== undefined && data[index] ? (
        <Row
          listRef={assignedRef}
          data={data[index]}
          itemSize={getPickItemSize(index)}
          style={style}
          startOnScroll={startOnScroll}
          resource={resource}
          page={page}
          type='pick'
        />
      ) : null,
    [getPickItemSize, resource],
  );

  const RenderDropRow = useCallback(
    ({ data, index, style }) =>
      index !== undefined && data[index] ? (
        <Row
          listRef={assignedRef}
          data={data[index]}
          itemSize={getDropItemSize(index)}
          style={style}
          startOnScroll={startOnScroll}
          resource={resource}
          page={page}
          type='drop'
        />
      ) : null,
    [getDropItemSize, resource],
  );

  useEffect(() => {
    if (assignedRef.current?.resetAfterIndex) {
      assignedRef.current.resetAfterIndex(0, true); // true = force re-render
    }
  }, [visibleDisplayLoadsAssignedPickStops, visibleDisplayLoadsAssignedDropStops]);

  const allPickStopsList = useMemo(
    () =>
      visibleDisplayLoadsAssignedPickStops && (
        <AutoSizer disableWidth>
          {({ height }) => (
            <>
              <VariableSizeList
                ref={assignedRef}
                outerRef={el => {
                  listReference.current = el;
                }}
                style={{ overflowX: 'initial', overflowY: 'initial' }}
                itemData={visibleDisplayLoadsAssignedPickStops}
                itemCount={visibleDisplayLoadsAssignedPickStops.length}
                height={height}
                itemSize={getPickItemSize}>
                {RenderPickRow}
              </VariableSizeList>
            </>
          )}
        </AutoSizer>
      ),
    [visibleDisplayLoadsAssignedPickStops, getPickItemSize, RenderPickRow],
  );

  const allDropStopsList = useMemo(
    () =>
      visibleDisplayLoadsAssignedDropStops && (
        <AutoSizer disableWidth>
          {({ height }) => (
            <>
              <VariableSizeList
                ref={assignedRef}
                outerRef={el => {
                  listReference.current = el;
                }}
                style={{ overflowX: 'initial', overflowY: 'initial' }}
                itemData={visibleDisplayLoadsAssignedDropStops}
                itemCount={visibleDisplayLoadsAssignedDropStops.length}
                height={height}
                itemSize={getDropItemSize}>
                {RenderDropRow}
              </VariableSizeList>
            </>
          )}
        </AutoSizer>
      ),
    [visibleDisplayLoadsAssignedDropStops, getDropItemSize, RenderDropRow],
  );

  const reactedPickOrders = state[resource].loadDetails?.pickStopAndOrders?.filter(order => order.rowState.type === 'order' && order.rowState.reacted);
  const reactedDropOrders = state[resource].loadDetails?.dropStopAndOrders?.filter(order => order.rowState.type === 'order' && order.rowState.reacted);
  const reactedOrders = reactedPickOrders?.length && reactedDropOrders?.length ? [...reactedPickOrders, ...reactedDropOrders] : 
                        reactedPickOrders?.length ? reactedPickOrders :
                        reactedDropOrders?.length ? reactedDropOrders : [];

  const noLumper = reactedOrders?.filter(order => order.rowState.orderData?.dropWhLumper === 1).length > 0;
  
  const debounced = debounce(condition => {
    if (condition === 'noSelectedOrder') {
        message.warning("Please choose an order", 1)
    } else if (condition === 'noLumper') {
        message.warning("It's not possible to add a lumper for this WH", 1)
    }
  }, 700);

  const handleOpenLumperModal = () => {
      if (reactedOrders.length < 1) {
          debounced('noSelectedOrder');
      } else if (noLumper) {
          debounced('noLumper');
      } else {
          reactedOrders.length && setLumperModalIsOpen(true);
      }
  };

  const content = (
    <div>
      <div>
        <div className='call-check__x-btn' onClick={() => setLoadDetailsModalIsOpen(false)}>X</div>
        <div className='load-details__load-info'>{loadDetailsData(loadDetails)}</div>
        <div className='load-status__and-notes'>
          <SelectAndNotes
              resource={resource}
              rowState={rowState}
              page={page}
              type='pick'
              loadId={loadId}
              >
          </SelectAndNotes>
        </div>
        <div className='container__header-and-loads'>         
          <div style={{ display: 'flex' }}>{header('pick')}</div>
          <div style={{ height: '130px', width: '100%' }}>{allPickStopsList}</div>
        </div>
        <div className='load-status__and-notes'>
          <SelectAndNotes
              resource={resource}
              rowState={rowState}
              page={page}
              type='drop'
              loadId={loadId}
              >
          </SelectAndNotes>
          {state[resource].loadDetails?.loadType === 3 && <div className='load-details__lumper-btn__container'>
            <div className='load-details__add-lumper-btn' onClick={() => handleOpenLumperModal()}>Add Lumper</div>
          </div>}
        </div>
        <div className='container__header-and-loads'>         
          <div style={{ display: 'flex' }}>{header('drop')}</div>
          <div style={{ height: '130px', width: '100%' }}>{allDropStopsList}</div>
        </div>
        {lumperModalIsOpen &&
            <LumperModal
                resource={resource}
                lumperModalIsOpen={lumperModalIsOpen}
                setLumperModalIsOpen={setLumperModalIsOpen}
                reactedOrders={reactedOrders}
                >
            </LumperModal>
        }
      </div>
    </div>
  );

  if (loadDetailsLoading) {
    return (
      <div style={{ width: '800px', height: '500px' }}>
        <Loading />
      </div>
    )
  };

  return <>{content}</>;
};

export default SpreadsheetDetails;
